import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { usePandaDocSyncStatus } from 'core/hooks';

import * as S from './PandaDocAnalytics.styled';

const fmtMoment = (value?: string | null): string =>
  value ? moment(value).format('DD.MM.YYYY HH:mm') : '—';

/**
 * Warns when the data is still incomplete: conversion / TTS analytics only
 * cover documents whose details have been pulled, and details sync is rate
 * limited (~50 docs/hour). Renders nothing once everything is synced.
 */
export const SyncBanner = memo(() => {
  const { t } = useTranslation();
  const { status } = usePandaDocSyncStatus();

  if (!status) {
    return null;
  }

  if (status.lastError) {
    return (
      <S.Banner variant="error">
        {t('pages.pandadocAnalytics.sync.lastError', {
          error: status.lastError,
        })}
      </S.Banner>
    );
  }

  const pending = status.detailsPending ?? 0;
  if (pending > 0) {
    return (
      <S.Banner variant="warn">
        <S.UpdatingRow style={{ marginTop: 0 }}>
          <S.Spinner />
          {t('pages.pandadocAnalytics.sync.updating')}
        </S.UpdatingRow>
        {t('pages.pandadocAnalytics.sync.detailsLoading', {
          pending,
          total: status.documentsCount ?? '—',
          date: fmtMoment(status.lastDetailsSyncAt),
        })}
      </S.Banner>
    );
  }

  return null;
});
